import type { EditorNode } from "@/models/TextEditor";
import type { FormulaOption } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";
import type { CstNode, IToken } from "chevrotain";

export interface ParseResult {
  tokens: EditorNode[];
  metadata: ParsedFormulaMetadata;
  errors: ParseError[];
}

export interface ParseError {
  type: "lexer" | "parser";
  message: string;
  line?: number;
  column?: number;
  offset?: number;
  length?: number;
}

export interface BracketPair {
  openingId: string;
  closingId: string;
  openingIndex: number;
  closingIndex: number;
  depth: number;
  color: string;
}

export interface FunctionCallMetadata {
  functionName: string;
  definition?: FormulaOption;
  startIndex: number;
  endIndex: number;
  argumentRanges: Array<{
    startIndex: number;
    endIndex: number;
    argumentIndex: number;
    parameterDef?: FormulaOption["parameters"][number];
  }>;
}

export interface ParsedFormulaMetadata {
  bracketPairs: Map<string, BracketPair>;
  functionCalls: FunctionCallMetadata[];
  totalTokens: number;
}

export interface CstChildrenDictionary {
  [key: string]: CstNode[] | IToken[] | undefined;
}

export interface TypedCstNode {
  name: string;
  children: CstChildrenDictionary;
}

export interface ExpressionCst {
  logicalExpression: CstNode[];
}

export interface BinaryExpressionCst {
  lhs: CstNode[];
  rhs?: CstNode[];
  Plus?: IToken[];
  Minus?: IToken[];
  Star?: IToken[];
  Slash?: IToken[];
  Percent?: IToken[];
  Caret?: IToken[];
  LessThan?: IToken[];
  GreaterThan?: IToken[];
  Equals?: IToken[];
  NotEquals?: IToken[];
  LessEquals?: IToken[];
  GreaterEquals?: IToken[];
  LogicalAnd?: IToken[];
  LogicalOr?: IToken[];
}

export interface AtomicExpressionCst {
  unaryExpression?: CstNode[];
  numberLiteral?: CstNode[];
  stringLiteral?: CstNode[];
  attributeReference?: CstNode[];
  parenthesizedExpression?: CstNode[];
  functionCall?: CstNode[];
  uppercaseIdentifier?: CstNode[];
}

export interface NumberLiteralCst {
  NumberLiteral: IToken[];
}

export interface StringLiteralCst {
  StringLiteral: IToken[];
}

export interface AttributeReferenceCst {
  AttributeName: IToken[];
  attributeContext?: CstNode[];
}

export interface UppercaseIdentifierCst {
  FunctionName: IToken[];
}

export interface ParenthesizedExpressionCst {
  LeftParenthesis: IToken[];
  expression: CstNode[];
  RightParenthesis: IToken[];
}

export interface FunctionCallCst {
  FunctionName?: IToken[];
  Plus?: IToken[];
  Minus?: IToken[];
  Star?: IToken[];
  Slash?: IToken[];
  Percent?: IToken[];
  Caret?: IToken[];
  LessThan?: IToken[];
  GreaterThan?: IToken[];
  Equals?: IToken[];
  NotEquals?: IToken[];
  LessEquals?: IToken[];
  GreaterEquals?: IToken[];
  LogicalAnd?: IToken[];
  LogicalOr?: IToken[];
  LeftParenthesis: IToken[];
  argumentList?: CstNode[];
  RightParenthesis: IToken[];
}

export interface ArgumentListCst {
  children: {
    argument: CstNode[];
    Comma?: IToken[];
  };
}

export interface VisitorContext {
  functionsByNameMap: Record<string, FormulaOption>;
  serviceAttributes: IAttribute[];
  bracketDepth: number;
  sequentialIndex: number;
}

export enum TokenTypeName {
  NumberLiteral = "NumberLiteral",
  StringLiteral = "StringLiteral",
  IncompleteString = "IncompleteString",
  InvalidIdentifier = "InvalidIdentifier",
  LeftParenthesis = "LeftParenthesis",
  RightParenthesis = "RightParenthesis",
  LeftBrace = "LeftBrace",
  RightBrace = "RightBrace",
  Comma = "Comma",
  FunctionName = "FunctionName",
  AttributeName = "AttributeName",
  WhiteSpace = "WhiteSpace",
  Plus = "Plus",
  Minus = "Minus",
  Star = "Star",
  Slash = "Slash",
  Percent = "Percent",
  Caret = "Caret",
  LessThan = "LessThan",
  GreaterThan = "GreaterThan",
  Equals = "Equals",
  NotEquals = "NotEquals",
  LessEquals = "LessEquals",
  GreaterEquals = "GreaterEquals",
  LogicalAnd = "LogicalAnd",
  LogicalOr = "LogicalOr",
  Unrecognized = "Unrecognized",
}
